import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import FormProvider from '@/components/HookForm/FormProvider';
import Label from '@/components/@IntranetLibrary/Label';
import { FormGroup } from '@/components/Board/CreateBoardModal/BoardForm/BoardFormField/style';
import { PreviewBox, SectionBox } from './style';
import RHFSelect from '@/components/HookForm/RHFSelect';
import {
  WORKING_HOURS_OPTIONS,
  WorkingHoursChangeProps,
} from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/WorkingHoursChange/@hooks/useWorkingHoursChangeProps';
import { useTranslation } from 'react-i18next';

type Props = Pick<
  WorkingHoursChangeProps,
  | 'formProps'
  | 'currentWorkingHours'
  | 'newWorkingHoursOptions'
  | 'startYearMonthOptions'
  | 'selectedNewWorkingHours'
  | 'selectedStartYearMonth'
>;

const WorkingHoursChange = ({
  formProps,
  currentWorkingHours,
  newWorkingHoursOptions,
  startYearMonthOptions,
  selectedNewWorkingHours,
  selectedStartYearMonth,
}: Props) => {
  const { t } = useTranslation();

  return (
    <div>
      {/* 안내 설명 */}
      <div style={{ padding: '24px 20px' }}>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            color: 'var(--dk-text-secondary, #888D96)',
          }}
        >
          {t(
            '근태 품의는 별도의 결재 없이 즉시 승인되며, 팀장을 포함한 소속 팀원에게 자동 공유됩니다.',
          )}
          <br />
          {t(
            '원활한 업무 진행을 위해 팀장 및 동료에게 사전 공유를 권장 드립니다.',
          )}
        </Typography>
        <Typography
          sx={{
            marginTop: '24px',
            fontSize: '14px',
            color: 'var(--dk-accent, #0066CC)',
            lineHeight: '20px',
            fontWeight: 400,
          }}
        >
          {t(
            '근무시간은 매월 1일 기준으로 변경되며, 월 1회 변경을 원칙으로 합니다.',
          )}
        </Typography>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            color: 'var(--dk-text-secondary, #888D96)',
          }}
        >
          {t('근무시간 변경은 전월 말일까지 신청해야 합니다.')}
        </Typography>

        <Typography
          sx={{
            marginTop: '24px',
            fontSize: '14px',
            color: 'var(--dk-danger, #F55060)',
            lineHeight: '20px',
            fontWeight: 500,
          }}
        >
          {t(
            '사실과 다르게 신청할 경우, 인사 규정에 따라 불이익이 발생할 수 있습니다.',
          )}
        </Typography>
      </div>

      <div
        style={{ width: '100%', height: '3px', backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)' }}
      />
      {/* 신청 폼 */}
      <FormProvider {...formProps}>
        <SectionBox>
          {/* 현재 근무 시간 */}
          <Typography
            sx={{
              fontSize: '16px',
              lineHeight: '24px',
              fontWeight: 500,
            }}
          >
            {t('신청 시간')}
          </Typography>
          <Stack gap="8px">
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '20px',
                color: 'var(--dk-text, #000)',
              }}
            >
              {t('현재 근무 시간')}
            </Typography>
            <Box
              sx={{
                width: '100%',
                backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
                borderRadius: '8px',
                padding: '12px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '20px',
                  color: 'var(--dk-text, #000)',
                }}
              >
                {currentWorkingHours}
              </Typography>
            </Box>
          </Stack>

          {/* 변경할 근무 시간 */}
          <FormGroup gapsize={'s'}>
            <Label isRequired={true} title={t('변경할 근무 시간')} size={14} />
            <RHFSelect
              name={'newWorkingHours'}
              options={newWorkingHoursOptions}
              placeholder={t('선택하세요')}
              sx={{ width: '100%' }}
            />
          </FormGroup>

          {/* 근무 시간 변경 시작 */}
          <FormGroup gapsize={'s'}>
            <Label
              isRequired={true}
              title={t('근무 시간 변경 시작')}
              size={14}
            />
            <RHFSelect
              name={'startYearMonth'}
              options={startYearMonthOptions}
              placeholder={t('선택하세요')}
              sx={{ width: '100%' }}
            />
          </FormGroup>

          {/* 변경 미리보기 */}
          {selectedNewWorkingHours && selectedStartYearMonth && (
            <PreviewBox>
              <Stack direction={'row'} alignItems={'center'} gap={'8px'}>
                <Typography sx={{ fontSize: '14px', color: 'var(--dk-text, #000)' }}>
                  {t('기존')} {currentWorkingHours}
                </Typography>
                <Typography sx={{ fontSize: '14px', color: 'var(--dk-text, #000)' }}>
                  →
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',
                    color: 'var(--dk-accent, #0066CC)',
                    fontWeight: 600,
                    lineHeight: '24px',
                  }}
                >
                  {t('변경')}{' '}
                  {WORKING_HOURS_OPTIONS.find(
                    (opt) => opt.value === selectedNewWorkingHours,
                  )?.label ?? selectedNewWorkingHours}
                </Typography>
              </Stack>
              <Typography
                sx={{ fontSize: ' 14px', color: 'var(--dk-text-secondary, #888D96)', lineHeight: '20px' }}
              >
                {selectedStartYearMonth}-01{t('부터 적용')}
              </Typography>
            </PreviewBox>
          )}
        </SectionBox>
      </FormProvider>
    </div>
  );
};

export default WorkingHoursChange;
